/* eslint-disable jsx-a11y/no-autofocus */

import React from 'react';
import pt from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import CodeMirror from '@uiw/react-codemirror';
import { xcodeLight, xcodeDark } from '@uiw/codemirror-theme-xcode';
import { json } from '@codemirror/lang-json';

import jsonLinter, { parseJSON, getErrorMessage } from './json-linter';
import jsonKeymap from './json-keymap';
import jsonCursor from './json-cursor';

export const DEFAULT_OBJECT_VALUE = '{\n  \n}';
export const DEFAULT_ARRAY_VALUE = '[\n  \n]';

const BASIC_SETUP = Object.freeze({
  lineNumbers: true,
  highlightActiveLineGutter: true,

  bracketMatching: true,
  closeBrackets: true,

  foldGutter: true,
  indentOnInput: true,

  highlightActiveLine: true,
  highlightSelectionMatches: true,

  defaultKeymap: false,
});

function getDefaultValue(value) {
  return Array.isArray(value) ? DEFAULT_ARRAY_VALUE : DEFAULT_OBJECT_VALUE;
}

function stringify(value) {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return '';
  }
}

export default class JsonEditor extends React.PureComponent {
  static propTypes = {
    id: pt.string,
    value: pt.oneOfType([pt.object, pt.array]),
    readOnly: pt.bool,
    autoFocus: pt.bool,
    onChange: pt.func,
    onValidate: pt.func,
  };

  static defaultProps = {
    id: 'json-editor',
    readOnly: false,
    autoFocus: false,
  };

  constructor(props) {
    super(props);

    const { value } = props;

    this.state = {
      value,
      editorValue: isEmpty(value) ? getDefaultValue(value) : stringify(value),
    };

    this.extensions = [json(), jsonLinter, jsonKeymap, jsonCursor()];
  }

  componentDidUpdate(prevProps) {
    const { value } = this.props;

    if (value !== prevProps.value && !isEqual(value, this.state.value)) {
      this.setState({
        value: value,
        editorValue: stringify(value),
      });
    }
  }

  onChange = (value) => {
    const { onChange, onValidate } = this.props;

    if (!onChange) {
      return;
    }

    const { json, errors } = parseJSON(value);

    // Show validation errors for non-empty invalid JSON
    if (errors.length > 0 && value.trim().length > 0) {
      const { error } = errors[0];

      return onValidate?.(false, getErrorMessage(error));
    }

    this.setState({ value: json }, () => {
      onValidate?.(true);
      onChange(json);
    });
  };

  getTheme() {
    const isDark =
      document.documentElement.getAttribute('data-theme') === 'dark';
    return isDark ? xcodeDark : xcodeLight;
  }

  render() {
    const { id, autoFocus, readOnly } = this.props;
    const { editorValue } = this.state;

    return (
      <CodeMirror
        id={id}
        value={editorValue}
        theme={this.getTheme()}
        readOnly={readOnly}
        autoFocus={autoFocus}
        onChange={this.onChange}
        extensions={this.extensions}
        basicSetup={BASIC_SETUP}
      />
    );
  }
}
